import BaseObject from './BaseObject';
import Occasion from '../helpers/Occasion';
import { ContactInfo, ContactInfoType, Coords, ID } from '../types';
import { differenceInYears } from 'date-fns';
import emailURL from '../helpers/preferred-email-client';

interface ComponentizedDate {
  day?: number;
  month?: number;
  year?: number;
}

export interface TeamMemberNetworkStrength {
  bucket: number;
  userID: string;
  score: number;
}

export const networkStrengthLabelForBucket = (bucket: number): string => {
  switch (bucket) {
    case 1:
      return 'High';
    case 2:
      return 'Medium';
    case 3:
      return 'Low';
    default:
      return '';
  }
};

const sortNetworkStrengths = (
  a: TeamMemberNetworkStrength,
  b: TeamMemberNetworkStrength,
): number => {
  if (a.bucket < b.bucket) {
    return -1;
  } else if (a.bucket > b.bucket) {
    return 1;
  } else if (a.score > b.score) {
    return -1;
  } else if (a.score < b.score) {
    return 1;
  } else {
    return 0;
  }
};

export const contactOptionsForContactInfo = (contactInfo: ContactInfo): ContactInfo[] => {
  if (contactInfo.type === 'phone') {
    return [
      {
        type: 'messages',
        value: contactInfo.value,
      },
      {
        type: 'phone',
        value: contactInfo.value,
      },
    ];
  }

  return [contactInfo];
};

const platforms = {
  facebook: 'facebookURL',
  linkedin: 'linkedinURL',
  instagram: 'instagramURL',
  twitter: 'twitterURL',
  github: 'githubURL',
  angellist: 'angellistURL',
  soundcloud: 'soundcloudURL',
  quora: 'quoraURL',
  youtube: 'youtubeURL',
  vimeo: 'vimeoURL',
  aboutme: 'aboutmeURL',
  tumblr: 'tumblrURL',
  pinterest: 'pinterestURL',
  slideshare: 'slideshareURL',
  reddit: 'redditURL',
  flickr: 'flickrURL',
  foursquare: 'foursquareURL',
  hackernews: 'hackernewsURL',
};

type Longitude = number;
type Latitude = number;

const monthAbbreviationMap = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].reduce((abbrevs, month) => {
  const date = new Date();
  date.setMonth(month, 1);
  const abbreviation = date.toLocaleString('default', { month: 'short' });
  return { ...abbrevs, [month + 1]: abbreviation }; // month is 0-indexed, so we add 1 to match the 1-indexed birthday.month
}, {});

export default class Contact extends BaseObject {
  declare id: ID;
  isSharedContact: boolean;
  avatarURL: string;
  created: Occasion;
  displayName: string;
  display_name: string; // this is a deprecated/legacy alias for the displayName property
  firstName: string;
  interactionType: string;
  isClayUser: boolean;
  isRestricted?: boolean;
  invited: boolean;
  relationship: string; // should constrain type here to possible relationship values, e.g. "closest"
  related: RelatedContact[];

  // TODO: we only want to read notes for a contact from what is in the notes slice of the redux store state.
  // Keeping notes on Contact satisfies legacy expectations, e.g. for isProfileEmpty fn. Use of notes directly
  // on Contact is deprecated. If we need to know about presence of notes on Contact, we should use another prop.
  notes: Array<any>;

  aboutmeURL?: string;
  age?: number;
  angellistURL?: string;
  bio?: string;
  birthday?: ComponentizedDate;
  memorializedDate?: ComponentizedDate;
  isMemorialized?: boolean;
  byline?: string;
  educations?: any; // vector of { name: string, degree?: string, end?: { year: number, month?: number }, start?: { year: number, month?: number } }
  facebookURL?: string;
  firstEmailDate?: Occasion;
  firstInteractionDate?: Occasion;
  firstMeetingDate?: Occasion;
  firstMentionDate?: Occasion;
  flickrURL?: string;
  foursquareURL?: string;
  gender?: string;
  githubURL?: string;
  gravtarURL?: string;
  hackernewsURL?: string;
  headline?: string;
  information?: ContactInfo[];
  instagramURL?: string;
  interests?: any; // vector of interests
  lastEmailDate?: Occasion;
  lastInteractionDate?: Occasion;
  lastMeetingDate?: Occasion;
  lastMentionDate?: Occasion;
  lastMessageDate?: Occasion;
  lastName?: string;
  linkedinFollowers?: number;
  linkedinFollowing?: number;
  linkedinURL?: string;
  location?: string;
  locations?: any; // vector of location objects
  locationCoordinates?: Coords;
  coordinate?: [Longitude, Latitude];
  lookup?: string;
  lookupType?: string; // 2 character code, e.g. "EM" for email
  mediumURL?: string;
  mediumUsername?: string;
  nextMeetingDate?: Occasion;
  numberOfEmailInteractions?: number;
  numberOfMessages?: number;
  numberOfMeetings?: number;
  objectID?: ID; // this is a deprecated/legacy alias for the id property
  organization?: string;
  organizations?: any; // vector of organization objects
  pinterestURL?: string;
  quoraURL?: string;
  redditURL?: string;
  pronouns?: string;
  reminder?: Occasion;
  score?: number;
  slideshareURL?: string;
  social: Array<any>;
  soundcloudURL?: string;
  source?: string; // 2 char code, e.g. "CL" for Clay. Should add type for these
  title?: string;
  tumblrURL?: string;
  twitterFollowers?: number;
  twitterFollowing?: number;
  twitterHandle?: string;
  twitterURL?: string;
  userID?: number;
  viewCount?: number;
  vimeoURL?: string;
  website?: string;
  websites?: any; // vector of website objects
  youtubeURL?: string;
  deleted?: Occasion;
  contact: null;
  contacts: null;
  notesLoaded: boolean;
  networkStrengthWithUserTeamMembers: TeamMemberNetworkStrength[];
  linkID?: string;
  sortFirstName?: string;
  sortLastName?: string;
  _snippetResult?: any;
  timestamp?: Occasion;
  informationLastUpdated?: Occasion;

  constructor(props: any = {}) {
    super(props);

    // Might reconsider doing this, but need a clearer idea of how all props are used in UI first.
    Object.keys(props).forEach((key) => {
      if (key === 'starred') {
        return;
      }

      try {
        this[key] = props[key];
      } catch {}
    });

    // set to the temp ID provisioned by BaseObject if objectID or id isn't set
    this.id = String(props.objectID || props.id || this.id);
    // this allows for using something other than the contact's own ID to link to a profile view in-app
    this.linkID = props.linkID;
    this.isSharedContact = false;
    this.avatarURL =
      props.avatar_url || props.avatarURL || props.avatar?.url || props.avatarUrl || '';
    this.created = props.created ? new Occasion(props.created) : new Occasion();
    this.displayName = props.displayName || props.display_name || props.name || '';
    this.firstName = props.first_name || props.firstName || '';
    this.sortFirstName = props.sortFirstName;
    this.lastName = props.last_name || props.lastName || '';
    this.sortLastName = props.sortLastName;
    // we pull this data from store.data.sources, but this is kept here for legacy usage in the Archive list sidebar
    this.information = props.information || [];
    this.interactionType = props.interaction_type || props.interactionType || '';
    this.isClayUser = props.is_clay_user || props.isClayUser || false;
    this.invited = props.invited || false;
    this.relationship = '';
    this.pronouns = props.pronouns || undefined;
    this.score = Math.floor(props.score || 0);
    this._snippetResult = props._snippetResult;
    this.source = props.source || '';
    this.social = props.social || [];
    this.related = (props.related || [])
      .filter(({ id }) => String(id) != this.id)
      .map((relatedContact) => new RelatedContact(relatedContact));

    this.locationCoordinates = props.locationCoordinates || null;
    if (this.locationCoordinates) {
      this.coordinate = [this.locationCoordinates.lon, this.locationCoordinates.lat];
    }

    // fix birthday thing
    let birthday: ComponentizedDate = props.birthday || {};
    if (props['birthday.day']) {
      birthday.day = props['birthday.day'];
      delete this['birthday.day'];
    }
    if (props['birthday.month']) {
      birthday.month = props['birthday.month'];
      delete this['birthday.month'];
    }
    if (props['birthday.year']) {
      birthday.year = props['birthday.year'];
      delete this['birthday.year'];
    }

    if (Object.keys(birthday).length > 0) {
      this.birthday = birthday;
    } else {
      this.birthday = null;
    }

    if ((!this.age || this.age === 0) && this.birthday?.year) {
      try {
        const bday = new Date(this.birthday.year, this.birthday.month - 1, this.birthday.day);
        this.age = differenceInYears(new Date(), bday);
      } catch (_) {
        this.age = 0;
      }
    }

    this.memorializedDate = props.memorializedDate || {};
    this.isMemorialized = !!props.isMemorialized;

    this.isRestricted = !!props.isRestricted || !!props.is_restricted;
    if (props.hasOwnProperty('isRestricted')) {
      this.isRestricted = props.isRestricted === false ? false : true;
    } else if (props.hasOwnProperty('is_restricted')) {
      this.isRestricted = props.is_restricted === false ? false : true;
    }

    // This use is deprecated. See note above.
    this.notes = props.notes || [];

    // allows us to clear reminders
    this.reminder = props.reminder ? new Occasion(props.reminder) : null;

    if (props.firstEmailDate) {
      this.firstEmailDate = new Occasion(props.firstEmailDate);
    }

    if (props.firstInteractionDate) {
      this.firstInteractionDate = new Occasion(props.firstInteractionDate);
    }

    if (props.firstMeetingDate) {
      this.firstMeetingDate = new Occasion(props.firstMeetingDate);
    }

    if (props.firstMentionDate) {
      this.firstMentionDate = new Occasion(props.firstMentionDate);
    }

    if (props.lastEmailDate) {
      this.lastEmailDate = new Occasion(props.lastEmailDate);
    }

    if (props.lastInteractionDate) {
      this.lastInteractionDate = new Occasion(props.lastInteractionDate);
    }

    if (props.lastMeetingDate) {
      this.lastMeetingDate = new Occasion(props.lastMeetingDate);
    }

    if (props.lastMessageDate) {
      this.lastMessageDate = new Occasion(props.lastMessageDate);
    }

    if (props.lastMentionDate) {
      this.lastMentionDate = new Occasion(props.lastMentionDate);
    }

    if (props.nextMeetingDate) {
      this.nextMeetingDate = new Occasion(props.nextMeetingDate);
    }

    if (props.users) {
      this.networkStrengthWithUserTeamMembers = (props.users || []).sort(sortNetworkStrengths);
    }

    if (props.deleted) {
      this.deleted = new Occasion(props.deleted);
    }

    this.notesLoaded = !!props.notesLoaded;

    if (props['@timestamp']) {
      this.timestamp = new Occasion(props['@timestamp']);
    } else if (props.timestamp) {
      this.timestamp = new Occasion(props.timestamp);
    }

    if (props.latestInformationDate || props.informationLastUpdated) {
      this.informationLastUpdated = props.latestInformationDate
        ? new Occasion(props.latestInformationDate)
        : new Occasion(props.informationLastUpdated);
    }
  }

  get link() {
    return this.isPersisted ? `/contact/${this.linkID || this.id}` : '';
  }

  get name() {
    if (this.firstName && this.lastName) {
      return this.firstName + ' ' + this.lastName;
    } else if (this.displayName) {
      return this.displayName;
    } else if (this.firstName || this.lastName) {
      return this.firstName || this.lastName;
    }
    return '';
  }

  get primaryLocation() {
    if (!this.locations || this.locations.length === 0) {
      return null;
    }

    if (this.locations.length === 1) {
      return this.locations[0];
    }

    return this.locations.find((item) => item.isPrimary) || this.locations[0];
  }

  get locationString() {
    const location = this.primaryLocation;
    return location?.formatted_address || location?.address || location?.formatted;
  }

  getContactInfoOfType(contactInfoType: ContactInfoType, count?: number): string | null {
    const contactInfoForType = this.information
      .filter(({ type }) => type === contactInfoType)
      .map(({ value }) => value);

    if (count && contactInfoForType.length > count) {
      return contactInfoForType.slice(0, count).join(',');
    }

    // NB - In Apple Messages, only the first phone number per comma separated list will be
    // picked up when pasting into a recipients field. Use newline separation to have all
    // numbers picked up.
    if (contactInfoForType.length > 0) {
      return contactInfoForType.join(',');
    }

    return null;
  }

  get email() {
    return this.getContactInfoOfType('email');
  }

  get linkedin() {
    return this.getContactInfoOfType('linkedin', 1);
  }

  get phone() {
    return this.getContactInfoOfType('phone');
  }

  get socialProfiles(): { platform: string; url: string }[] {
    return Object.entries(platforms).reduce((profiles, [platform, key]) => {
      if (!this[key as keyof Contact]) {
        return profiles;
      }

      return [...profiles, { platform, url: this[key as keyof Contact] }];
    }, [] as { platform: string; url: string }[]);
  }

  get birthMonthAbbreviated(): string | null {
    if (!this.birthday?.month) {
      return null;
    }

    return monthAbbreviationMap[this.birthday.month];
  }

  get formattedBirthday(): string | null {
    if (!this.birthday?.month || !this.birthday?.day) {
      return null;
    }

    return `${this.birthMonthAbbreviated} ${this.birthday.day}`;
  }

  get lifespan(): string {
    if (!this.birthday?.year || !this.memorializedDate?.year) {
      return null;
    }

    return `${this.birthday.year} - ${this.memorializedDate.year}`;
  }

  isMoreRecentlyUpdatedThan(contact: Contact): boolean {
    if (!contact) {
      return true;
    }

    if (!this.timestamp && contact.timestamp) {
      return false;
    }

    if (!this.timestamp && !contact.timestamp) {
      return true;
    }

    if (this.timestamp && !contact.timestamp) {
      return true;
    }

    return contact.timestamp?.isBefore(this.timestamp);
  }

  copyEmailToClipboard() {
    return () => {
      const emails = this.email;

      if (!!emails) {
        navigator.clipboard.writeText(emails);
      }
    };
  }

  copyPhoneNumberToClipboard() {
    return () => {
      const phoneNumbers = this.phone;

      if (!!phoneNumbers) {
        navigator.clipboard.writeText(phoneNumbers);
      }
    };
  }
}

export class RelatedContact extends BaseObject {
  displayName?: string;
  avatarURL?: string;
  bidirectional?: boolean;

  constructor(props) {
    super(props);
    this.displayName = props.displayName;
    this.avatarURL = props.avatarURL;
    this.bidirectional = props.bidirectional;
  }
}

const mailtoHref = (email: string, isFreePlan: boolean): string =>
  emailURL({ recipient: email, body: isFreePlan && encodeURIComponent('\n\nSent via clay.earth') });

const hrefForContactInfo = (contactInfo: ContactInfo, isFreePlan: boolean) => {
  switch (contactInfo.type) {
    case 'twitter':
      return `https://twitter.com/${contactInfo.value}`;
    case 'instagram':
      return `https://instagram.com/${contactInfo.value}`;
    case 'linkedin':
      return `https://linkedin.com/in/${contactInfo.value}`;
    case 'facebook':
      return `https://www.facebook.com/${contactInfo.value}`;
    case 'email':
      return mailtoHref(contactInfo.value, isFreePlan);
    case 'url':
      return contactInfo.value;
    case 'phone':
      return `tel:${contactInfo.value}`;
    case 'messages':
      return `sms:${contactInfo.value}`;
    default:
      return undefined;
  }
};

const targetForContactInfo = (contactInfo: ContactInfo) => {
  let blankTargetTypes = ['twitter', 'linkedin', 'facebook', 'instagram', 'url', 'email'];

  if (window.app?.isElectron && window.desktopIntegrations) {
    blankTargetTypes = ['twitter', 'linkedin', 'facebook', 'instagram', 'url'];
  }

  return blankTargetTypes.includes(contactInfo.type) ? '_blank' : undefined;
};

const displayValueForContactInfo = (contactInfo: ContactInfo) => {
  if (contactInfo.type === 'instagram' || contactInfo.type === 'twitter') {
    return `@${contactInfo.value}`;
  }

  return contactInfo.value;
};

const DEFAULT_EMAIL_LABEL = 'email';
const DEFAULT_PHONE_LABEL = 'phone';
export const defaultEmailLabelOptions = [DEFAULT_EMAIL_LABEL, 'home', 'work', 'main'];
export const defaultPhoneLabelOptions = [DEFAULT_PHONE_LABEL, 'iPhone', 'home', 'work', 'main'];

export const defaultLabelOptionsForSource = (contactInfoType: ContactInfoType) => {
  switch (contactInfoType) {
    case 'email':
      return defaultEmailLabelOptions;
    case 'phone':
      return defaultPhoneLabelOptions;
    default:
      return [];
  }
};

export const defaultLabelForSource = (contactInfoType: ContactInfoType) => {
  switch (contactInfoType) {
    case 'email':
      return DEFAULT_EMAIL_LABEL;
    case 'phone':
      return DEFAULT_PHONE_LABEL;
    default:
      return '';
  }
};

const labelForContactInfo = (contactInfo: ContactInfo) => {
  if (contactInfo.type === 'email' && !contactInfo.label) {
    return DEFAULT_EMAIL_LABEL;
  } else if (contactInfo.type === 'phone' && !contactInfo.label) {
    return DEFAULT_PHONE_LABEL;
  }

  return contactInfo.label;
};

export const attributesForContactInfo = (contactInfo: ContactInfo, isFreePlan: boolean) => ({
  href: hrefForContactInfo(contactInfo, isFreePlan),
  target: targetForContactInfo(contactInfo),
  displayValue: displayValueForContactInfo(contactInfo),
  label: labelForContactInfo(contactInfo),
});
